import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import PitcherFatigue from './PitcherFatigue';
import { getCardRarityClass } from '../utils/cardUtils';
import { getEffectivePitcherStamina } from '../helpers/cardHelpers';

/**
 * Proactive roster substitution panel shown on the Create Game page.
 * Displays ALL starting players with optional substitution dropdowns.
 *
 * - Tired players (isAvailable === false) are highlighted amber and
 *   pre-populated with the best available substitute.
 * - Fresh players show a "Keep original" dropdown — user may optionally
 *   pick a substitute for any position.
 * - Substitute pool is built from the team's own cards (within-team only):
 *     Batter positions → bench cards that can fill the position
 *     SP/RP slots      → any other pitcher on the team
 * - Fatigued cards are excluded from the substitute pool.
 * - Deduplication: a card picked for one slot is removed from all other options.
 *
 * Props:
 *   teamLineup            - UserTeamLineup[] from the selected userTeam
 *   inventoryData         - CardInventory[] returned by fetchUserCardInventory (has .Card and .Fatigue)
 *   loading               - boolean, true while fetching inventory
 *   onSubstitutionsChange - callback(substitutions[]) with [{fromInventoryID, toInventoryID}]
 */

/** Mirrors canFillTeamPosition from the backend CardFatigueService. */
const canFillPosition = (position, card) => {
    const normalized = position.toUpperCase().trim();
    const cardPositions = card.positions
        ? card.positions.split(',').map(p => p.toUpperCase().trim())
        : [];
    if (['SP', 'RP'].includes(normalized)) return card.type === 'Pitcher' && cardPositions.includes(normalized);
    if (normalized === 'BN' || normalized === 'BENCH') return card.type === 'Batter';
    if (normalized === 'DH') return card.type === 'Batter';
    if (normalized === 'OF') return cardPositions.includes('OF');
    if (['LF', 'CF', 'RF'].includes(normalized)) return cardPositions.includes('OF') || cardPositions.includes(normalized);
    return cardPositions.includes(normalized);
};

const RosterSubstitutionPanel = ({ teamLineup, inventoryData, loading, onSubstitutionsChange }) => {
    // { [fromInventoryID]: toInventoryID } — empty string means "keep original"
    const [picks, setPicks] = useState({});

    // cardID → inventory entry
    const inventoryByCardID = useMemo(() => {
        const map = new Map();
        for (const ci of (inventoryData ?? [])) {
            map.set(String(ci.cardID), ci);
        }
        return map;
    }, [inventoryData]);

    // cardID → team position
    const positionByCardID = useMemo(() => {
        const map = new Map();
        for (const ul of (teamLineup ?? [])) {
            if (ul.cardID) map.set(String(ul.cardID), ul.position ?? 'Bench');
        }
        return map;
    }, [teamLineup]);

    // All starter entries (non-BN) with inventory data attached
    const allStarters = useMemo(() => {
        return (teamLineup ?? [])
            .filter(ul => ul.cardID && ul.position && ul.position !== 'BN')
            .map(ul => {
                const ci = inventoryByCardID.get(String(ul.cardID));
                if (!ci?.Card) return null;
                return {
                    position: ul.position,
                    inventoryID: String(ci.inventoryID),
                    cardID: String(ci.cardID),
                    card: ci.Card,
                    upgrades: ci.upgrades ?? null,
                    fatigue: ci.Fatigue,
                    isAvailable: ci.Fatigue?.isAvailable !== false,
                };
            })
            .filter(Boolean);
    }, [teamLineup, inventoryByCardID]);

    const pitcherStarters = useMemo(
        () => allStarters.filter(s => ['SP', 'RP'].includes(s.position) && !s.isAvailable),
        [allStarters]
    );
    const batterStarters = useMemo(
        () => allStarters.filter(s => !['SP', 'RP'].includes(s.position) && !s.isAvailable),
        [allStarters]
    );

    /** Build the substitute pool for one starter (available within-team cards only). */
    const buildPool = (starter) => {
        const isPitcherPos = ['SP', 'RP'].includes(starter.position);
        const activeInventoryIDs = new Set(allStarters.map(s => s.inventoryID));
        return (inventoryData ?? [])
            .filter(ci => {
                if (!ci.Card) return false;
                if (activeInventoryIDs.has(String(ci.inventoryID))) return false;
                if (ci.Fatigue?.isAvailable === false) return false;
                const ciPos = positionByCardID.get(String(ci.cardID));
                if (isPitcherPos) {
                    const cardPositions = ci.Card.positions
                        ? ci.Card.positions.split(',').map(p => p.toUpperCase().trim())
                        : [];
                    return cardPositions.includes(starter.position.toUpperCase());
                }
                // Team bench players, or non-team batter cards not in the lineup
                const isTeamCard = ciPos !== undefined;
                if (isTeamCard) return ciPos === 'Bench' && canFillPosition(starter.position, ci.Card);
                return ci.Card.type === 'Batter' && canFillPosition(starter.position, ci.Card);
            })
            .map(ci => ({
                inventoryID: String(ci.inventoryID),
                name: ci.Card.name,
                rating: ci.Card.rating,
                positions: ci.Card.positions,
            }))
            .sort((a, b) => b.rating - a.rating);
    };

    // Auto-pick best substitute for tired players when inventoryData changes.
    useEffect(() => {
        if (!inventoryData || !teamLineup) {
            setPicks({});
            onSubstitutionsChange([]);
            return;
        }

        const tiredStarters = allStarters.filter(s => !s.isAvailable);
        const newPicks = {};
        const usedIDs = new Set();

        for (const starter of tiredStarters) {
            const pool = buildPool(starter);
            const best = pool.find(sub => !usedIDs.has(sub.inventoryID));
            if (best) {
                usedIDs.add(best.inventoryID);
                newPicks[starter.inventoryID] = best.inventoryID;
            }
        }

        setPicks(newPicks);
        const subs = Object.entries(newPicks).map(([from, to]) => ({ fromInventoryID: from, toInventoryID: to }));
        onSubstitutionsChange(subs);
    }, [inventoryData]); // Only re-run when inventory data changes (team selection change)

    const handlePickChange = (fromInventoryID, toInventoryID) => {
        const newPicks = { ...picks };
        if (toInventoryID) {
            newPicks[fromInventoryID] = toInventoryID;
        } else {
            delete newPicks[fromInventoryID];
        }
        setPicks(newPicks);
        const subs = Object.entries(newPicks).map(([from, to]) => ({ fromInventoryID: from, toInventoryID: to }));
        onSubstitutionsChange(subs);
    };

    /** Options for one row: exclude cards picked for other rows (dedup). */
    const getOptionsForRow = (fromInventoryID, pool) => {
        const otherPicks = new Set(
            Object.entries(picks)
                .filter(([key]) => key !== fromInventoryID)
                .map(([, val]) => val)
        );
        return pool.filter(sub => !otherPicks.has(sub.inventoryID));
    };

    if (loading) {
        return <div className="roster-sub-panel roster-sub-loading">Checking player availability…</div>;
    }

    if (!inventoryData || inventoryData.length === 0) return null;
    if (pitcherStarters.length === 0 && batterStarters.length === 0) return null;

    const renderRow = (starter) => {
        const pool = buildPool(starter);
        const options = getOptionsForRow(starter.inventoryID, pool);
        const currentPick = picks[starter.inventoryID] ?? '';
        const isPitcherCard = starter.card.type === 'Pitcher';
        const currentFatigue = starter.fatigue?.currentFatigue ?? 0;
        const stamina = isPitcherCard
            ? getEffectivePitcherStamina(starter.card, starter.upgrades)
            : (starter.card.stamina ?? 27);
        const needsRest = !starter.isAvailable;

        return (
            <div key={starter.inventoryID} className={`roster-sub-row${needsRest ? ' needs-rest' : ''}`}>
                <span className="roster-sub-position-badge">{starter.position}</span>
                <div className="roster-sub-player-info">
                    {starter.card.rating && (
                        <span className={`roster-sub-card-rating ${getCardRarityClass(starter.card.rating)}`}>{starter.card.rating}</span>
                    )}
                    <span className="roster-sub-player-name">{starter.card.name}</span>
                    {starter.card.positions && (
                        <span className="roster-sub-card-positions">{starter.card.positions}</span>
                    )}
                    {isPitcherCard && currentFatigue > 0 && (
                        <span className="tooltip-container">
                            <PitcherFatigue pitcherWork={currentFatigue} stamina={stamina} showLabel />
                            <span className="tooltip">{currentFatigue}/{stamina} fatigue</span>
                        </span>
                    )}
                    {needsRest && <span className="roster-sub-needs-rest-badge">Needs rest</span>}
                </div>
                <span className="roster-sub-arrow" aria-hidden="true">→</span>
                <div className="roster-sub-pick">
                    {needsRest && options.length === 0 ? (
                        <span className="roster-sub-no-sub">No substitutes available</span>
                    ) : (
                        <select
                            value={currentPick}
                            onChange={e => handlePickChange(starter.inventoryID, e.target.value)}
                            aria-label={`Substitute for ${starter.card.name}`}
                        >
                            <option value="">Keep original</option>
                            {options.map(sub => (
                                <option key={sub.inventoryID} value={sub.inventoryID}>
                                    {sub.name} — {sub.rating} — {sub.positions}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="roster-sub-panel">
            {pitcherStarters.length > 0 && (
                <div className="roster-sub-section">
                    <span className="roster-sub-section-label">Pitchers</span>
                    {pitcherStarters.map(renderRow)}
                </div>
            )}
            {batterStarters.length > 0 && (
                <div className="roster-sub-section">
                    <span className="roster-sub-section-label">Batters</span>
                    {batterStarters.map(renderRow)}
                </div>
            )}
        </div>
    );
};

RosterSubstitutionPanel.propTypes = {
    teamLineup: PropTypes.arrayOf(
        PropTypes.shape({
            cardID: PropTypes.any,
            position: PropTypes.string,
        })
    ),
    inventoryData: PropTypes.arrayOf(
        PropTypes.shape({
            inventoryID: PropTypes.any,
            cardID: PropTypes.any,
            Card: PropTypes.shape({
                name: PropTypes.string,
                type: PropTypes.string,
                positions: PropTypes.string,
                stamina: PropTypes.number,
                rating: PropTypes.number,
            }),
            Fatigue: PropTypes.shape({
                currentFatigue: PropTypes.number,
                isAvailable: PropTypes.bool,
                availabilityReason: PropTypes.string,
            }),
        })
    ),
    loading: PropTypes.bool,
    onSubstitutionsChange: PropTypes.func.isRequired,
};

export default RosterSubstitutionPanel;
